import { useEffect, useRef } from 'react'
import { Icon } from '@/shared/ui/Icon'
import {
  DRAFT_CONVERSATION_ID,
  useConversations,
} from '@/features/conversations/ConversationsProvider'
import { MessageBubble } from './MessageBubble'

/**
 * 7.8I — conversation viewport.
 *
 * Hardened states:
 * - loading: shimmering skeleton placeholder (replaces the "Carregando…"
 *   text bubble that used to flash inline)
 * - empty: friendly card inviting the user to ask the first question
 * - error: branded error card instead of an assistant bubble pretending to
 *   speak
 * - sending: animated typing indicator under the live assistant slot
 * - sendError: dedicated error card (red), distinct from the assistant bubble
 *
 * Auto-scroll: every time the message list or sending state changes, we
 * snap the bottom sentinel into view. ``behavior: 'smooth'`` keeps the UX
 * subtle when answers stream in.
 */
export function ChatThread() {
  const {
    activeConversationId,
    startNewConsultation,
    messagesByConversation,
    messagesStatusByConversation,
    messagesErrorByConversation,
    sending,
    sendError,
    selectedAssistantMessageId,
    selectAssistantMessage,
  } = useConversations()

  const endRef = useRef<HTMLDivElement>(null)
  const messages =
    activeConversationId === DRAFT_CONVERSATION_ID
      ? []
      : messagesByConversation[activeConversationId] ?? []
  const status =
    activeConversationId === DRAFT_CONVERSATION_ID
      ? ('idle' as const)
      : messagesStatusByConversation[activeConversationId] ?? 'idle'
  const error =
    activeConversationId === DRAFT_CONVERSATION_ID
      ? null
      : messagesErrorByConversation[activeConversationId] ?? null

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' })
  }, [messages.length, sending, sendError, activeConversationId])

  if (activeConversationId === DRAFT_CONVERSATION_ID) {
    return null
  }

  if (status === 'loading' && messages.length === 0) {
    return (
      <div className="thread">
        <div className="thread-skeleton" aria-busy="true" aria-live="polite">
          <div className="skeleton-line long" />
          <div className="skeleton-line mid" />
          <div className="skeleton-line short" />
          <div className="skeleton-line long" />
          <div className="skeleton-line mid" />
        </div>
      </div>
    )
  }

  if (status === 'error' && messages.length === 0) {
    return (
      <div className="thread">
        <div className="send-error-card" role="alert">
          <Icon name="close" className="send-error-icon" />
          <span>
            <b>Não foi possível carregar as mensagens.</b>
            <br />
            {error ?? 'Tente novamente em instantes.'}
          </span>
        </div>
      </div>
    )
  }

  if (messages.length === 0 && !sending) {
    return (
      <div className="thread">
        <div className="thread-empty" role="status">
          <div className="empty-mark" aria-hidden="true">
            <Icon name="sparkles" />
          </div>
          <h4>Nenhuma mensagem nesta consulta ainda</h4>
          <p>
            Use o campo abaixo para perguntar — ou inicie uma{' '}
            <button
              type="button"
              className="empty-link"
              onClick={startNewConsultation}
            >
              nova consulta
            </button>
            .
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="thread" aria-live="polite">
      {messages.map((m) => (
        <MessageBubble
          key={m.id}
          message={m}
          selected={m.id === selectedAssistantMessageId}
          onSelect={
            m.role === 'assistant' ? () => selectAssistantMessage(m.id) : undefined
          }
        />
      ))}
      {sending && (
        <div className="msg assistant">
          <div className="answer">
            <span className="typing-indicator" aria-label="Assistente está respondendo">
              <span />
              <span />
              <span />
            </span>
          </div>
        </div>
      )}
      {sendError && !sending && (
        <div className="send-error-card" role="alert">
          <Icon name="close" className="send-error-icon" />
          <span>
            <b>Falha ao enviar.</b> {sendError}
          </span>
        </div>
      )}
      <div ref={endRef} aria-hidden="true" style={{ height: 1 }} />
    </div>
  )
}
